import { Cloudinary } from "@cloudinary/url-gen";
import { format } from "@cloudinary/url-gen/actions/delivery";
import { limitFit } from "@cloudinary/url-gen/actions/resize";
import { auto } from "@cloudinary/url-gen/qualifiers/format";
import { forwardRef, useMemo } from "react";

import DefaultAvatar from "@/components/default-avatar";
import { cn } from "@/lib/utils";

export const GAME_FALLBACK_IMAGE_ID = "store/s/default.webp";

interface CImageProps extends React.ComponentProps<"img"> {
  rW?: number;
}

interface GameImageSetType {
  gameimageSet?: {
    imageId?: string;
  }[];
}

interface ImageWithGameProps extends CImageProps {
  game: {
    name: string;
  } & GameImageSetType;
}

interface ImageWithLocationProps extends CImageProps {
  location: {
    company: {
      name: string;
    };
    displayname?: string;
    games: GameImageSetType[];
  };
}

interface ImageWithUserProps extends CImageProps {
  size: number;
  user: {
    imageId?: string;
    username: string;
  };
}

interface ImageWithImageIdProps extends CImageProps {
  imageId: string;
}

type CloudinaryImageProps =
  | ImageWithImageIdProps
  | ImageWithGameProps
  | ImageWithLocationProps
  | ImageWithUserProps;

export default forwardRef<
  HTMLImageElement | HTMLDivElement,
  CloudinaryImageProps
>(function CloudinaryImage({ alt, className, rW, ...props }, ref) {
  // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: its ok
  const { imageProps, src, title } = useMemo(() => {
    let imageId = GAME_FALLBACK_IMAGE_ID;
    const imageProps: React.ComponentProps<"img"> = { ...props };
    let title = "";

    if ("imageId" in props) {
      imageId = props.imageId;
      // biome-ignore lint/suspicious/noExplicitAny: cuz copied
      (imageProps as any).imageId = undefined;
    } else if ("game" in props) {
      if (props.game.gameimageSet) {
        const validImage = props.game.gameimageSet.find((i) => !!i.imageId);

        if (validImage) {
          imageId = validImage.imageId ?? "";
        }
      }
      title = props.game.name;
      // biome-ignore lint/suspicious/noExplicitAny: cuz copied
      (imageProps as any).game = undefined;
    } else if ("location" in props) {
      const validGame = props.location.games.find(
        (g) => !!g.gameimageSet && g.gameimageSet.some((i) => !!i.imageId)
      );

      if (validGame) {
        const validImage = validGame.gameimageSet?.find((i) => !!i.imageId);

        if (validImage) {
          imageId = validImage.imageId ?? "";
        }
      }
      title = props.location.displayname || props.location.company.name;
      // biome-ignore lint/suspicious/noExplicitAny: cuz copied
      (imageProps as any).location = undefined;
    } else if ("user" in props) {
      if (props.user.imageId) {
        imageId = props.user.imageId;
      }

      title = `@${props.user.username}`;
      // biome-ignore lint/suspicious/noExplicitAny: cuz copied
      (imageProps as any).size = undefined;
      // biome-ignore lint/suspicious/noExplicitAny: cuz copied
      (imageProps as any).user = undefined;
    }

    let img = new Cloudinary({
      cloud: {
        cloudName: process.env.CLOUDINARY_CLOUD_NAME,
      },
      url: {
        analytics: false,
      },
    }).image(imageId);

    if (rW) {
      img = img.resize(limitFit().width(rW));
    }

    const src = img.delivery(format(auto())).toURL();

    return {
      imageProps,
      src: "user" in props && imageId === GAME_FALLBACK_IMAGE_ID ? null : src,
      title,
    };
    // biome-ignore lint/correctness/useExhaustiveDependencies: props shouldnt change that often
  }, [props, rW]);

  if ("user" in props && !src) {
    return (
      <DefaultAvatar
        className={className}
        ref={ref}
        size={props.size}
        word={props.user.username}
      />
    );
  }

  return (
    <img
      alt={alt || title}
      className={cn("object-cover object-center", className)}
      loading="lazy"
      ref={ref as React.ForwardedRef<HTMLImageElement>}
      src={src ?? undefined}
      {...imageProps}
    />
  );
});
